/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus, Edit, Save, Trash2, RefreshCw, Eye, X,
  Music, Clock, Zap, AlertCircle, CheckCircle 
} from 'lucide-react';
import { BackingTrack, TimelineEntry, GenerateTimelineRequest, TimelinePattern } from '../../types/jamTrack';
import { 
  generateTimeline, 
  fetchAllTimelines, 
  updateTimeline, 
  deleteTimeline,
  regenerateTimeline 
} from '../../api/timelineAdmin';

const TIMELINE_PATTERNS: TimelinePattern[] = [
  '1-chord-per-bar',
  '2-bars-per-chord',
  'fast-changes',
  'waltz-pattern',
  'blues-12-bar',
  'country-ballad',
  'rock-progression'
];

const GENRE_OPTIONS = [
  'Rock', 'Pop', 'Blues', 'Jazz', 'Country', 'Folk', 
  'Metal', 'Punk', 'Alternative', 'Classical', 'R&B'
];

const STATUS_OPTIONS = ['pending', 'processing', 'completed', 'failed'];

const TimelineAdmin: React.FC = () => {
  const [timelines, setTimelines] = useState<BackingTrack[]>([]);
  const [selectedTimeline, setSelectedTimeline] = useState<BackingTrack | null>(null);
  const [editingTimeline, setEditingTimeline] = useState<BackingTrack | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'generate' | 'edit'>('list');

  // Generation form state
  const [generateForm, setGenerateForm] = useState<GenerateTimelineRequest>({
    youTubeUrl: '',
    key: '',
    scale: '',
    genre: '',
    bpm: undefined,
    expectedChords: [],
    pattern: '1-chord-per-bar',
    startOffsetSeconds: 0
  });

  // Additional form states for new features
  const [repeatedChords, setRepeatedChords] = useState<string>(''); // For generate form
  const [editRepeatedChords, setEditRepeatedChords] = useState<string>(''); // For edit form
  const [timelineJson, setTimelineJson] = useState<string>(''); // For direct JSON editing
  const [editTimelineJson, setEditTimelineJson] = useState<string>(''); // For edit JSON editing
  const [originalEditTimelineJson, setOriginalEditTimelineJson] = useState<string>(''); // Track original JSON
  const [editTabMode, setEditTabMode] = useState<'entries' | 'json'>('entries'); // Edit mode toggle

  // Load timelines on component mount
  useEffect(() => {
    loadTimelines();
  }, []);

  const loadTimelines = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchAllTimelines();
      setTimelines(data);
    } catch (err) {
      setError('Failed to load timelines');
      console.error('Error loading timelines:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateTimeline = async () => {
    if (!generateForm.youTubeUrl) {
      setError('YouTube URL is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const newTimeline = await generateTimeline({
        ...generateForm,
        expectedChords: generateForm.expectedChords?.filter(chord => chord.trim() !== '')
      });
      
      setTimelines(prev => [newTimeline, ...prev]);
      setGenerateForm({
        youTubeUrl: '',
        key: '',
        scale: '',
        genre: '',
        bpm: undefined,
        expectedChords: [],
        pattern: '1-chord-per-bar',
        startOffsetSeconds: 0
      });
      setActiveTab('list');
    } catch (err) {
      setError('Failed to generate timeline');
      console.error('Error generating timeline:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTimeline = (timeline: BackingTrack) => {
    setEditingTimeline({ ...timeline });
    
    // Initialize repeated chords from title
    const regex = /\|\s*([A-G#bmMajor\s]+)\s*\|/;
    const match = timeline.title?.match(regex);
    if (match) {
      setEditRepeatedChords(match[1].trim());
    } else {
      setEditRepeatedChords('');
    }
    
    // Initialize JSON editing with current timeline
    const initialJson = JSON.stringify(timeline.timeline, null, 2);
    setEditTimelineJson(initialJson);
    setOriginalEditTimelineJson(initialJson);
    
    setActiveTab('edit');
  };

  const handleSaveTimeline = async () => {
    if (!editingTimeline) return;
    
    // If in JSON edit mode, parse and update timeline from JSON
    if (editTabMode === 'json') {
      try {
        const parsedTimeline = JSON.parse(editTimelineJson);
        // Validate that it's an array of timeline entries
        if (Array.isArray(parsedTimeline)) {
          setEditingTimeline(prev => prev ? { ...prev, timeline: parsedTimeline } : null);
        }
      } catch (err) {
        setError('Invalid JSON format in timeline');
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    try {
      // Store the current form values before API calls
      const currentFormValues = {
        pattern: editingTimeline.pattern,
        startOffsetSeconds: editingTimeline.startOffsetSeconds,
        title: editingTimeline.title,
        genre: editingTimeline.genre,
        bpm: editingTimeline.bpm,
        status: editingTimeline.status,
        detectionConfidence: editingTimeline.detectionConfidence,
        supportedScales: editingTimeline.supportedScales
      };

      // Prepare update request with TimelineJson only if it was actually modified
      const hasJsonChanges = editTabMode === 'json' && editTimelineJson !== originalEditTimelineJson;
      
      console.log('Debug save:', {
        editTabMode,
        hasJsonChanges,
        jsonLength: editTimelineJson.length,
        originalJsonLength: originalEditTimelineJson.length
      });
      
      const updateRequest: any = {
        ...editingTimeline
      };
      
      // Only add TimelineJson if it was actually modified
      if (hasJsonChanges) {
        updateRequest.TimelineJson = editTimelineJson;
      }
      
      // First update the timeline properties
      const updatedTimeline = await updateTimeline(editingTimeline.id, updateRequest);
      
      // Check if pattern or startOffsetSeconds changed - if so, regenerate the timeline
      const originalTimeline = timelines.find(t => t.id === editingTimeline.id);
      const patternChanged = originalTimeline && originalTimeline.pattern !== editingTimeline.pattern;
      const startOffsetChanged = originalTimeline && originalTimeline.startOffsetSeconds !== editingTimeline.startOffsetSeconds;
      
      let finalTimeline = updatedTimeline;
      
      // Regenerate timeline only if pattern changed (not just start offset)
      // Start offset changes are now handled automatically by the backend
      if (patternChanged && editTabMode === 'entries' && !hasJsonChanges) {
        // Only regenerate if we're in entries mode (not JSON mode)
        // Use repeated chords if provided, otherwise extract from title
        let chordsToUse: string[] = [];
        
        if (editRepeatedChords.trim()) {
          // Use manually entered repeated chords
          chordsToUse = editRepeatedChords.trim().split(/\s+/).filter(chord => chord);
        } else {
          // Extract clean chord progression from title
          const regex = /\|\s*([A-G#bmMajor\s]+)\s*\|/;
          const match = editingTimeline.title?.match(regex);
          if (match) {
            chordsToUse = match[1].trim().split(/\s+/).filter(chord => chord);
          } else {
            // Fallback to common progression
            chordsToUse = ['C', 'F', 'Am', 'G'];
          }
        }
        
        // Regenerate timeline with clean chord progression
        finalTimeline = await regenerateTimeline(editingTimeline.id, {
          pattern: editingTimeline.pattern || '2-bars-per-chord',
          startOffsetSeconds: editingTimeline.startOffsetSeconds || 0,
          expectedChords: chordsToUse
        });
        
        // Preserve the form values after regeneration
        finalTimeline = {
          ...finalTimeline,
          ...currentFormValues
        };
      }
      
      // Update the timelines list
      setTimelines(prev => 
        prev.map(t => t.id === finalTimeline.id ? finalTimeline : t)
      );
      
      // Keep the editing form open with preserved values
      setEditingTimeline(finalTimeline);
      // Don't switch tabs - stay on edit tab
    } catch (err) {
      setError('Failed to save timeline');
      console.error('Error saving timeline:', err);
    } finally {
      setIsLoading(false);
    }
  };


  const handleDeleteTimeline = async (timelineId: string) => {
    if (!window.confirm('Are you sure you want to delete this timeline?')) return;

    setIsLoading(true);
    setError(null);
    try {
      await deleteTimeline(timelineId);
      setTimelines(prev => prev.filter(t => t.id !== timelineId));
      if (selectedTimeline?.id === timelineId) {
        setSelectedTimeline(null);
      }
      if (editingTimeline?.id === timelineId) {
        setEditingTimeline(null);
        setActiveTab('list');
      }
    } catch (err) {
      setError('Failed to delete timeline');
      console.error('Error deleting timeline:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTimelineEntry = (index: number, field: keyof TimelineEntry, value: any) => {
    if (!editingTimeline) return;
    
    const updatedTimeline = { ...editingTimeline };
    updatedTimeline.timeline[index] = {
      ...updatedTimeline.timeline[index],
      [field]: value
    };
    setEditingTimeline(updatedTimeline);
  };

  const addTimelineEntry = () => {
    if (!editingTimeline) return;
    
    const lastEntry = editingTimeline.timeline[editingTimeline.timeline.length - 1];
    const newEntry: TimelineEntry = {
      chordInstance: editingTimeline.timeline.length + 1,
      startTime: lastEntry ? lastEntry.endTime : 0,
      endTime: lastEntry ? lastEntry.endTime + 4 : 4,
      chord: 'C'
    };
    
    setEditingTimeline({
      ...editingTimeline,
      timeline: [...editingTimeline.timeline, newEntry]
    });
  };

  const deleteTimelineEntry = (index: number) => {
    if (!editingTimeline) return;
    
    setEditingTimeline({
      ...editingTimeline,
      timeline: editingTimeline.timeline.filter((_, i) => i !== index)
    });
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'processing': return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'failed': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Timeline Administration</h1>
          <p className="text-muted-foreground">
            Generate, edit, and manage backing track timelines
          </p>
        </div>

        {error && (
          <Card className="mb-6 border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <p>{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={(value: any) => setActiveTab(value)}>
          <TabsList className="mb-6">
            <TabsTrigger value="list">
              <Music className="h-4 w-4 mr-2" />
              Timeline List
            </TabsTrigger>
            <TabsTrigger value="generate">
              <Plus className="h-4 w-4 mr-2" />
              Generate New
            </TabsTrigger>
            {editingTimeline && (
              <TabsTrigger value="edit">
                <Edit className="h-4 w-4 mr-2" />
                Edit Timeline
              </TabsTrigger>
            )}
          </TabsList>

          {/* Timeline List Tab */}
          <TabsContent value="list">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Existing Timelines ({timelines.length})</h2>
                <Button onClick={loadTimelines} disabled={isLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              <div className="grid gap-4">
                {timelines.map((timeline) => (
                  <Card key={timeline.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">
                              {timeline.title || `Timeline ${timeline.id.slice(0, 8)}`}
                            </h3>
                            <Badge className={getStatusColor(timeline.status)}>
                              <div className="flex items-center gap-1">
                                {getStatusIcon(timeline.status)}
                                {timeline.status}
                              </div>
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>YouTube ID: {timeline.youTubeVideoId}</span>
                            {timeline.genre && <span>Genre: {timeline.genre}</span>}
                            {timeline.bpm && <span>BPM: {timeline.bpm}</span>}
                            <span>{timeline.timeline.length} chords</span>
                          </div>
                          
                          {timeline.supportedScales.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {timeline.supportedScales.map((scale, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {scale.key} {scale.scaleType}
                                  {scale.confidence && ` (${Math.round(scale.confidence * 100)}%)`}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedTimeline(timeline)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditTimeline(timeline)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteTimeline(timeline.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Generate New Timeline Tab */}
          <TabsContent value="generate">
            <Card>
              <CardHeader>
                <CardTitle>Generate New Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="youtube-url">YouTube URL *</Label>
                      <Input
                        id="youtube-url"
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={generateForm.youTubeUrl}
                        onChange={(e) => setGenerateForm(prev => ({ ...prev, youTubeUrl: e.target.value }))}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="key">Key</Label>
                        <Input
                          id="key"
                          placeholder="C, Dm, F#"
                          value={generateForm.key || ''}
                          onChange={(e) => setGenerateForm(prev => ({ ...prev, key: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="scale">Scale</Label>
                        <Input
                          id="scale"
                          placeholder="Major, Minor"
                          value={generateForm.scale || ''}
                          onChange={(e) => setGenerateForm(prev => ({ ...prev, scale: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="genre">Genre</Label>
                        <Select value={generateForm.genre || ''} onValueChange={(value) => 
                          setGenerateForm(prev => ({ ...prev, genre: value }))
                        }>
                          <SelectTrigger>
                            <SelectValue placeholder="Select genre" />
                          </SelectTrigger>
                          <SelectContent>
                            {GENRE_OPTIONS.map(genre => (
                              <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="bpm">BPM</Label>
                        <Input
                          id="bpm"
                          type="number"
                          placeholder="120"
                          value={generateForm.bpm || ''}
                          onChange={(e) => setGenerateForm(prev => ({ 
                            ...prev, 
                            bpm: e.target.value ? parseInt(e.target.value) : undefined 
                          }))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="pattern">Timeline Pattern</Label>
                      <Select value={generateForm.pattern || '1-chord-per-bar'} onValueChange={(value: TimelinePattern) => 
                        setGenerateForm(prev => ({ ...prev, pattern: value }))
                      }>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMELINE_PATTERNS.map(pattern => (
                            <SelectItem key={pattern} value={pattern}>
                              {pattern.replace(/-/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="start-offset">Start Offset (seconds)</Label>
                      <Input
                        id="start-offset"
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="0"
                        value={generateForm.startOffsetSeconds || 0}
                        onChange={(e) => setGenerateForm(prev => ({ 
                          ...prev, 
                          startOffsetSeconds: parseFloat(e.target.value) || 0 
                        }))}
                      />
                    </div>

                    <div>
                      <Label htmlFor="repeated-chords">Repeated Chords (space-separated)</Label>
                      <Input
                        id="repeated-chords"
                        placeholder="C F Am G (space-separated)"
                        value={repeatedChords}
                        onChange={(e) => {
                          setRepeatedChords(e.target.value);
                          // Update generateForm.expectedChords for backward compatibility
                          const chords = e.target.value.trim().split(/\s+/).filter(chord => chord);
                          setGenerateForm(prev => ({ ...prev, expectedChords: chords }));
                        }}
                      />
                    </div>

                    <div>
                      <Label htmlFor="timeline-json">Timeline JSON (optional - leave empty for auto-generation)</Label>
                      <Textarea
                        id="timeline-json"
                        placeholder='[{"chordInstance": 1, "startTime": 0, "endTime": 4, "chord": "C"}]'
                        value={timelineJson}
                        onChange={(e) => setTimelineJson(e.target.value)}
                        rows={6}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="flex justify-end">
                  <Button onClick={handleGenerateTimeline} disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Generate Timeline
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Edit Timeline Tab */}
          {editingTimeline && (
            <TabsContent value="edit">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Timeline Properties</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="edit-title">Title</Label>
                        <Input
                          id="edit-title"
                          value={editingTimeline.title || ''}
                          onChange={(e) => setEditingTimeline(prev => 
                            prev ? { ...prev, title: e.target.value } : null
                          )}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-genre">Genre</Label>
                        <Select value={editingTimeline.genre || ''} onValueChange={(value) => 
                          setEditingTimeline(prev => prev ? { ...prev, genre: value } : null)
                        }>
                          <SelectTrigger>
                            <SelectValue placeholder="Select genre" />
                          </SelectTrigger>
                          <SelectContent>
                            {GENRE_OPTIONS.map(genre => (
                              <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit-status">Status</Label>
                        <Select value={editingTimeline.status} onValueChange={(value) => 
                          setEditingTimeline(prev => prev ? { ...prev, status: value } : null)
                        }>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(status => (
                              <SelectItem key={status} value={status}>{status}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="edit-key">Key</Label>
                        <Input
                          id="edit-key"
                          value={editingTimeline.supportedScales[0]?.key || ''}
                          onChange={(e) => {
                            const newKey = e.target.value;
                            setEditingTimeline(prev => {
                              if (!prev) return null;
                              const updatedScales = [...prev.supportedScales];
                              if (updatedScales[0]) {
                                updatedScales[0] = { ...updatedScales[0], key: newKey };
                              } else {
                                updatedScales[0] = { key: newKey, scaleType: 'Major', confidence: 0.9 };
                              }
                              return { ...prev, supportedScales: updatedScales };
                            });
                          }}
                          placeholder="e.g. C, G, Am, F#"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-scale">Scale/Mode</Label>
                        <Input
                          id="edit-scale"
                          value={editingTimeline.supportedScales[0]?.scaleType || ''}
                          onChange={(e) => {
                            const newScale = e.target.value;
                            setEditingTimeline(prev => {
                              if (!prev) return null;
                              const updatedScales = [...prev.supportedScales];
                              if (updatedScales[0]) {
                                updatedScales[0] = { ...updatedScales[0], scaleType: newScale };
                              } else {
                                updatedScales[0] = { key: 'C', scaleType: newScale, confidence: 0.9 };
                              }
                              return { ...prev, supportedScales: updatedScales };
                            });
                          }}
                          placeholder="e.g. Major, Minor, Dorian, Mixolydian"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-bpm">BPM</Label>
                        <Input
                          id="edit-bpm"
                          type="number"
                          value={editingTimeline.bpm || ''}
                          onChange={(e) => setEditingTimeline(prev => 
                            prev ? { ...prev, bpm: parseInt(e.target.value) || undefined } : null
                          )}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="edit-pattern">Pattern</Label>
                        <Select 
                          value={editingTimeline.pattern || '2-bars-per-chord'} 
                          onValueChange={(value: TimelinePattern) => 
                            setEditingTimeline(prev => prev ? { ...prev, pattern: value } : null)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select pattern" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIMELINE_PATTERNS.map(pattern => (
                              <SelectItem key={pattern} value={pattern}>{pattern}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit-offset">Start Offset (seconds)</Label>
                        <Input
                          id="edit-offset"
                          type="number"
                          step="0.1"
                          min="0"
                          value={editingTimeline.startOffsetSeconds || 0}
                          onChange={(e) => setEditingTimeline(prev => 
                            prev ? { ...prev, startOffsetSeconds: parseFloat(e.target.value) || 0 } : null
                          )}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-confidence">Detection Confidence</Label>
                        <Input
                          id="edit-confidence"
                          type="number"
                          step="0.01"
                          min="0"
                          max="1"
                          value={editingTimeline.detectionConfidence || ''}
                          onChange={(e) => setEditingTimeline(prev => 
                            prev ? { ...prev, detectionConfidence: parseFloat(e.target.value) || undefined } : null
                          )}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="edit-description">Description</Label>
                      <Textarea
                        id="edit-description"
                        value={editingTimeline.description || ''}
                        onChange={(e) => setEditingTimeline(prev => 
                          prev ? { ...prev, description: e.target.value } : null
                        )}
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="edit-repeated-chords">Repeated Chords (for regeneration)</Label>
                      <Input
                        id="edit-repeated-chords"
                        placeholder="C F Am G"
                        value={editRepeatedChords}
                        onChange={(e) => setEditRepeatedChords(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Used for timeline regeneration when pattern or offset changes. Leave empty to auto-extract from title.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 py-4">
                  <Button variant="outline" onClick={() => {
                    setEditingTimeline(null);
                    setActiveTab('list');
                  }}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button onClick={handleSaveTimeline} disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Saving & Regenerating...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Timeline
                      </>
                    )}
                  </Button>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Timeline Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={editTabMode} onValueChange={(value: 'entries' | 'json') => setEditTabMode(value)}>
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="entries">Timeline Entries ({editingTimeline.timeline.length})</TabsTrigger>
                        <TabsTrigger value="json">JSON Editor</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="entries" className="mt-4">
                        <div className="flex justify-end mb-4">
                          <Button onClick={addTimelineEntry} size="sm">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Entry
                          </Button>
                        </div>
                    <div className="space-y-4">
                      {editingTimeline.timeline.map((entry, index) => (
                        <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                          <div className="flex-1 grid grid-cols-4 gap-4">
                            <div>
                              <Label className="text-xs">Chord</Label>
                              <Input
                                value={entry.chord}
                                onChange={(e) => updateTimelineEntry(index, 'chord', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Start Time</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={entry.startTime}
                                onChange={(e) => updateTimelineEntry(index, 'startTime', parseFloat(e.target.value))}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">End Time</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={entry.endTime}
                                onChange={(e) => updateTimelineEntry(index, 'endTime', parseFloat(e.target.value))}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Duration</Label>
                              <div className="text-sm text-muted-foreground pt-2">
                                {formatTime(entry.endTime - entry.startTime)}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteTimelineEntry(index)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                      </TabsContent>
                      
                      <TabsContent value="json" className="mt-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label htmlFor="edit-timeline-json">Timeline JSON</Label>
                            {editTimelineJson !== originalEditTimelineJson && (
                              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                                Modified
                              </Badge>
                            )}
                          </div>
                          <Textarea
                            id="edit-timeline-json"
                            value={editTimelineJson}
                            onChange={(e) => setEditTimelineJson(e.target.value)}
                            rows={20}
                            className="font-mono text-sm"
                            placeholder="Edit timeline JSON directly..."
                          />
                          <div className="flex justify-between items-start mt-2">
                            <p className="text-xs text-muted-foreground">
                              Direct JSON editing - changes will be applied when you save. Repeated chords setting is ignored in JSON mode.
                            </p>
                            {editTimelineJson !== originalEditTimelineJson && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setEditTimelineJson(originalEditTimelineJson);
                                }}
                              >
                                Reset JSON
                              </Button>
                            )}
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>

              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Timeline Preview Modal/Panel */}
        {selectedTimeline && (
          <Card className="mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Timeline Preview: {selectedTimeline.title || 'Untitled'}</CardTitle>
              <Button variant="outline" onClick={() => setSelectedTimeline(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                {selectedTimeline.timeline.map((entry, index) => (
                  <div key={index} className="p-2 border rounded text-center text-sm">
                    <div className="font-semibold">{entry.chord}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TimelineAdmin;